import { type FC, useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import VerificationCode from 'features/VerificationCode/VerificationCode'
import { disconnectSocialMedia, logoutUser, setPassword } from 'services/endpoints/auth'
import { deleteUser } from 'services/endpoints/profile'
import './_deleteAccountModal.scss'
import { useUserProfile } from 'store'


const DeleteAccountModal: FC<{
    mediaName?: string | any
    disconnectAccout?: boolean
    type?: string
    email?: string
    password?: string
}> = ({ type, disconnectAccout, mediaName, email, password }) => {
    const queryClient = useQueryClient()
    const userData = useUserProfile()!
    const [typeActive, setTypeActive] = useState<string | any>('')
    const [socialMediaState, setSocialMediaState] = useState<{
        twitter_connected: boolean
        google_connected: boolean
    }>({
        twitter_connected: !!userData.twitter,
        google_connected: !!userData.google
    })
    const [reason, setReason] = useState<{
        active: boolean
        text: string
    }>({
        active: false,
        text: ''
    })

    const [code, setCode] = useState('')

    const { t } = useTranslation()
    const modalData = useModalContext()

    useEffect(() => {
        setTypeActive(type)
    }, [type])

    const submitFunction = () => {
        if (code.trim() === '' || code.trim().length < 6) {
            addToast('error', t('pleaseInputVerificationCode'))
            return
        }

        addToast('loading', t('deletingAccount'))
        deleteUser({ delete_reason: reason.text, code }).then(resp => {
            toast.dismiss()
            if (resp.message?.original?.message === 'Wrong code!') {
                addToast('error', t('invalidCode'))
                return
            }

            modalData.clearModal()
            addToast('success', t('accountDeleted'))
            logoutUser('model') // fix later
        })
    }

    const confirmDisconnect = async (
        mediaName: string | any,
        email: string | any
    ) => {
        const toastId = addToast(
            'loading',
            t('disconnectingYourSocialMediaAccount')
        )
        if (userData.has_password === 0) {
            setPassword({
                password: password!,
                password_confirmation: password!
            })
        }
            queryClient.invalidateQueries('user-profile')
        const res = await disconnectSocialMedia(mediaName, email)
        toast.dismiss(toastId)
        if (res) {
            addToast('success', t('socialMediaAccountDisconnectedSuccessfully'))
            setSocialMediaState({
                ...socialMediaState,
                [`${mediaName}_connected`]: false
            })
            queryClient.invalidateQueries('user-profile')
        }
        modalData.clearModal()
    }

    if (typeActive === 'delete') {
        return (
            <>
                <div className='deleteAccountModal__description'>
                    {t('warningOnceYouDeleteYourAccount')}
                </div>
                <div
                    className={`deleteAccountModal__reason deleteAccountModal__reason--${reason.active ? 'active' : 'inactive'
                        }`}
                >
                    <p
                        onClick={() => {
                            setReason({ active: !reason.active, text: '' })
                        }}
                    >
                        {t('addReason')}
                    </p>
                    <InputCard
                        isTextArea={true}
                        type='text'
                        label={`${t('reason')}...`}
                        value={reason.text}
                        changeFn={(val: string) => {
                            setReason({ ...reason, text: val })
                        }}
                        customClass='deleteAccountModal__reason__input'
                    />
                </div>
                <VerificationCode changeFn={setCode} />
                <div className='deleteAccountModal__description deleteAccountModal__description--bot'>
                    {t('youCanDeactivateYourAccount')}
                </div>
                <div className='deleteAccountModal__buttons'>
                    <Button
                        text={t('deleteAccount')}
                        color='transparent'
                        type='transparent--black'
                        font='mont-14-normal'
                        height='4'
                        width='100'
                        clickFn={() => {
                            submitFunction()
                        }}
                    />
                    {/* <button className="deleteAccountModal__button deleteAccountModal__button--white">
                         Delete Account
                     </button>
                     <button className="deleteAccountModal__button deleteAccountModal__button--grey">
                     Deactivate
                      </button> */}
                </div>
            </>
        )
    } else if (typeActive === 'UpdateEmail') {
        return (
            <>
                <div className=''>
                    <Heading>
                        {t('Update Your Email')}
                    </Heading>
                </div>
                <VerificationCode changeFn={setCode} email={email} />
                <div className='deleteAccountModal__buttons'>
                    <Button
                        text={t('update')}
                        color='transparent'
                        type='transparent--black'
                        font='mont-14-normal'
                        height='3'
                        width='100'
                        clickFn={() => {
                            confirmDisconnect(mediaName, email)
                        }}
                    />
                </div>
            </>
        )
    } else {
        return (
            <>
                <div className='deleteAccountModal__description'>
                    {t('youCanDeactivateYourAccount')}
                </div>
                <div
                    className={`deleteAccountModal__reason deleteAccountModal__reason--${reason.active ? 'active' : 'inactive'
                        }`}
                >
                    <p
                        onClick={() => {
                            setReason({ active: !reason.active, text: '' })
                        }}
                    >
                        {t('addReason')}
                    </p>
                    <InputCard
                        isTextArea={true}
                        type='text'
                        label={`${t('reason')}...`}
                        value={reason.text}
                        changeFn={(val: string) => {
                            setReason({ ...reason, text: val })
                        }}
                        customClass='deleteAccountModal__reason__input'
                    />
                </div>
                <VerificationCode />
                <div className='deleteAccountModal__buttons'>
                    <Button
                        text={t('deactivate')}
                        color='black'
                        font='mont-16-bold'
                        width='20'
                        height='5'
                        disabled={true}
                    />
                </div>
            </>
        )
    }
}

export default DeleteAccountModal

const Heading = styled.div`
    display:flex;
    justify-content:center;
    font-size: 18px;
    font-weight: 600;
   `